import React, { useContext, useState } from 'react';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import CalculateIcon from '@mui/icons-material/Calculate';
import MapResults from 'components/generics/Map/MapResults';
import ResultsInfo from 'components/generics/Map/ResultsInfo';
import bioLogo from 'assets/images/bio.png';
import logoCover from 'assets/images/logo-cover.png';
import logoCoverDarkGreen from 'assets/images/logo-cover-dark-green.png';
import logoCoverTransparent from 'assets/images/logo-cover-transparent.png';
import DataGridParcelSynthesis from '../Datagrid/DataGridParcelSynthesis';
import './styles/section-parcels.style.scss';
import { sortWithLocale } from 'utils/tools_functions';
import { RenderConstraintNotRespected } from '../../Component/RenderConstraintNotRespected';
import { Button, ThemeProvider } from '@mui/material';
import { ExploitationContext } from 'utils/context';
import { customButtonTheme } from 'assets/styles/themes/generic_button_mui';
import { useTranslation } from 'react-i18next';

const ParcelLegend = ({
  hasParcelOrganic,
  isCoverProposalASelectedConstraint,
  isNitrogenASelectedConstraint,
}) => {
  const { t } = useTranslation();
  if (!isCoverProposalASelectedConstraint && !hasParcelOrganic) return null;

  return (
    <div className="legend-synthesis">
      {isCoverProposalASelectedConstraint && (
        <div>
          {isNitrogenASelectedConstraint && (
            <div className="legend-item">
              <img src={logoCover} alt="Logo couvert" className="legend-icon" />
              {t('simulation.result.common.legumeCover')}
            </div>
          )}
          <div className="legend-item">
            <img src={logoCoverDarkGreen} alt="Logo couvert" className="legend-icon" />
            {t('simulation.result.common.nonLegumeCover')}
          </div>
          <div className="legend-item">
            <img src={logoCoverTransparent} alt="Logo couvert" className="legend-icon" />
            {t('simulation.result.common.otherCover')}
          </div>
        </div>
      )}
      {hasParcelOrganic && (
        <div>
          <div className="legend-item">
            <SyncAltIcon className="legend-icon" />
            {t('simulation.result.common.bioConversion')}
          </div>
          <div className="legend-item">
            <img src={bioLogo} alt={t('alt.bioLogo')} className="legend-icon" />
            {t('simulation.common.bioCulture')}
          </div>
        </div>
      )}
    </div>
  );
};

const MapLegend = ({ hasParcelOrganic }) => {
  const { t } = useTranslation();
  if (!hasParcelOrganic) return null;

  return (
    <div className="legend">
      <div className="legend-title">{t('label.mapParcelLegend')}</div>
      <div className="legend-row">
        <div className="legend-item">
          <div className="legend-stroke conversion"></div>
          <p>{t('simulation.result.common.bioConversion')}</p>
        </div>
        <div className="legend-item">
          <div className="legend-stroke organic"></div>
          <p>{t('simulation.common.bioCulture')}</p>
        </div>
        <div className="legend-item">
          <div className="legend-stroke conventional"></div>
          <p>{t('label.conventionalCulture')}</p>
        </div>
      </div>
    </div>
  );
};

const hasConstraint = (configConstraints, name) =>
  configConstraints?.some((c) => c.name === name || c[name] === true);

const SectionParcels = ({
  forecastCampaign,
  suggest,
  setSuggest,
  result,
  exploitationParcels,
  coordsExist,
  mapData,
  editedSynthesisCellsRef,
  launchCalculette,
  hasChange,
  setHasChange,
  configConstraints,
}) => {
  const [isCalculating, setIsCalculating] = useState(false);
  const { t } = useTranslation();

  const { exploitationContext } = useContext(ExploitationContext);
  const {
    mapTab,
    setMapTab,
    mapYear,
    setMapYear,
    modalMap,
    setModalMap,
    editData,
    getMapParcelsData,
    setModalData,
    updateFromMap,
  } = mapData;

  const hasParcelOrganic = suggest?.parcels?.some(
    (p) => p.organicState === 'organic' || p.organicState === 'conversion',
  );

  const isCoverProposalASelectedConstraint = hasConstraint(configConstraints, 'coverProposal');
  const isNitrogenASelectedConstraint = hasConstraint(configConstraints, 'nitrogen');
  const isExtraBiomassASelectedConstraint = hasConstraint(configConstraints, 'extraBiomass');

  const buildColumns = () => {
    const baseColumns = [
      { field: 'parcel', headerName: t('common.parcel') },
      { field: 'surface', headerName: t('common.surface') },
    ];
    const yearColumns = Array(suggest?.years?.length + 1 || 0)
      .fill()
      .map((_, i) => {
        const year = `${i + forecastCampaign?.startYear - 1}-${i + forecastCampaign?.endYear - 1}`;
        return { field: year, headerName: year };
      });
    const extraColumns = isExtraBiomassASelectedConstraint
      ? [
          {
            field: 'avgDryMatterPerCampaign',
            headerName: t('simulation.result.common.averageBiomassInput'),
          },
        ]
      : [];

    return [...baseColumns, ...yearColumns, ...extraColumns];
  };

  const buildRows = () => {
    const sortParcels = sortWithLocale(suggest.parcels, 'name');
    return (
      sortParcels.map((selectedParcel) => {
        const parcelData = exploitationParcels?.find((parcel) => parcel.id === selectedParcel.id);
        const cultureName =
          parcelData?.parcelCultures?.[
            `${forecastCampaign?.startYear - 1}-${forecastCampaign?.endYear - 1}`
          ]?.name;
        const organicState = parcelData?.organicState;

        const childrenList = selectedParcel.isParent
          ? exploitationParcels
              ?.filter((parcel) => parcel.parent === selectedParcel.id)
              .map((child) => ({
                surface: child.surface,
                previousCulture1:
                  child.parcelCultures?.[
                    `${forecastCampaign?.startYear - 1}-${forecastCampaign?.endYear - 1}`
                  ]?.name || t('message.noCultureToDisplay'),
              }))
          : [];

        const children = [];
        (childrenList || []).forEach((child) => {
          const existingGroup = children.find(
            (group) => group.previousCulture1 === child.previousCulture1,
          );
          if (existingGroup) {
            existingGroup.surface += child.surface;
          } else {
            children.push({ previousCulture1: child.previousCulture1, surface: child.surface });
          }
        });

        return {
          ...selectedParcel,
          previousCulture1: cultureName || t('message.noCultureToDisplay'),
          organicState,
          children,
        };
      }) || []
    );
  };

  const formatDataCsv = (culturesDistribution, indexYear = 0) => {
    return culturesDistribution
      .filter((item) => item.hectares > 0)
      .map((item) => ({
        culture: item.culture.name,
        surface: item.hectares.toFixed(2).replace('.', ','),
        margeBrute: item.grossMargin.toFixed(2).replace('.', ','),
        tonnage: item.tonnage.toFixed(2).replace('.', ','),
        year: forecastCampaign.startYear + indexYear,
      }));
  };

  const exportCsv = () => {
    let csvData = [];

    csvData.push(formatDataCsv(result.previous.culturesDistribution, 0));

    suggest.years.forEach((yearResult, indexYear) => {
      csvData.push(formatDataCsv(yearResult.culturesDistribution, indexYear + 1));
    });

    csvData = csvData.flat();

    const csvString = [
      ['culture', 'surface', 'marge_brute', 'tonnage', 'année'],
      ...csvData.map((item) => [
        item.culture,
        item.surface,
        item.margeBrute,
        item.tonnage,
        item.year,
      ]),
    ]
      .map((row) => row.join(';'))
      .join('\n');

    const blob = new Blob(['\ufeff' + csvString], { type: 'text/csv; charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Suggestion_culture_${exploitationContext.name}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const handleRecalculate = async () => {
    setIsCalculating(true);
    try {
      await launchCalculette();
    } finally {
      setIsCalculating(false);
    }
  };
  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: 'fit-content',
          marginLeft: '80px',
        }}
      >
        {suggest?.years.map((y, idx) => {
          return y.errorsConstraints
            ? Object.keys(y.errorsConstraints).map((constraintName) => {
                if (constraintName === 'coversConstraint') {
                  return (
                    <RenderConstraintNotRespected
                      year={y}
                      index={idx}
                      constraintName={constraintName}
                      constraintDetailName={'coversConstraint'}
                      suggest={suggest}
                      result={result}
                      forecastCampaign={forecastCampaign}
                    />
                  );
                } else {
                  return Object.keys(y.errorsConstraints[constraintName]).map(
                    (constraintDetailName) => {
                      return (
                        <RenderConstraintNotRespected
                          year={y}
                          index={idx}
                          constraintName={constraintName}
                          constraintDetailName={constraintDetailName}
                          suggest={suggest}
                          result={result}
                          forecastCampaign={forecastCampaign}
                        />
                      );
                    },
                  );
                }
              })
            : null;
        })}
      </div>
      <div className="resultContent">
        <section className="section">
          <div className="section-header">
            <h2 className="section-title">{t('title.parcelsSynthesis')}</h2>
            <div className="section-actions">
              <button
                className="recalculate-btn"
                onClick={handleRecalculate}
                disabled={isCalculating || !hasChange}
              >
                <CalculateIcon />
                {isCalculating ? t('button.recalculating') : t('button.recalculate')}
              </button>
              <div className="view-toggle">
                <button
                  className={`toggle-btn ${!mapTab ? 'active' : ''}`}
                  onClick={() => setMapTab(false)}
                >
                  {t('button.table')}
                </button>
                <button
                  className={`toggle-btn ${mapTab ? 'active' : ''}`}
                  onClick={() => setMapTab(true)}
                  disabled={!coordsExist}
                >
                  {t('common.map')}
                </button>
              </div>
            </div>
            <ThemeProvider theme={customButtonTheme}>
              <Button defaultStyle="button-synthesis" onClick={() => exportCsv()}>
                {t('button.exportCsv')}
              </Button>
            </ThemeProvider>
          </div>

          {coordsExist && (
            <div style={{ display: mapTab ? 'block' : 'none' }}>
              <MapLegend hasParcelOrganic={hasParcelOrganic} />
              <MapResults
                hasCulturesList={true}
                parcelUpdated={null}
                setModalData={setModalData}
                data={getMapParcelsData()}
                nbYear={result?.nbYear}
                setMapYear={setMapYear}
                center={mapTab}
                suggestions={suggest?.years}
                previousCropMargin={result?.previous?.culturesDistribution}
                forecastCampaign={forecastCampaign}
              />
            </div>
          )}

          {modalMap && (
            <ResultsInfo
              modal={modalMap}
              setModal={setModalMap}
              updateFromMap={updateFromMap}
              resultsInfo={editData}
              cultures={result?.cultures}
              year={mapYear}
            />
          )}

          <div style={{ display: mapTab ? 'none' : 'block' }}>
            <ParcelLegend
              hasParcelOrganic={hasParcelOrganic}
              isCoverProposalASelectedConstraint={isCoverProposalASelectedConstraint}
              isNitrogenASelectedConstraint={isNitrogenASelectedConstraint}
            />

            <DataGridParcelSynthesis
              columns={buildColumns()}
              rows={buildRows()}
              cultures={result?.cultures}
              irrigationInformation={suggest?.years}
              setHasChange={setHasChange}
              suggest={suggest}
              setSuggest={setSuggest}
              editedSynthesisCellsRef={editedSynthesisCellsRef}
              isNitrogenASelectedConstraint={isNitrogenASelectedConstraint}
            />
            <p className="legend-note">
              <span>*</span> {t('label.parentParcelOfGrouping')}.
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

export default SectionParcels;
